import React, { useState, useRef, useCallback, useEffect } from 'react';
import { ArrowLeft, GripHorizontal, Monitor, MonitorOff, Trash2 } from 'lucide-react';
import BingoBallDraw from '../components/BingoBallDraw';
import LoteriaCallerDraw from '../components/LoteriaCallerDraw';
import AutoMarkupInput from '../components/AutoMarkupInput';

export default function BingoCallerPage({
  onBack, canvasImage, isDarkMode, onBallDrawn, onCallerReset, gameMode,
  handleAutoMarkupSubmit, autoMarkupResetKey, calledNumbers,
  loteriaFigures,
  callerBgVideoSrc, callerBgVideoOpacity = 60, callerBgVideoFit = 'cover',
  obsMode = false,
}) {
  const [callerPercent, setCallerPercent] = useState(30);
  const [callerMode, setCallerMode] = useState(gameMode === 'loteria' ? 'loteria' : 'bingo');

  // Sync callerMode when user switches game mode in the main view
  useEffect(() => {
    setCallerMode(gameMode === 'loteria' ? 'loteria' : 'bingo');
  }, [gameMode]);
  const containerRef = useRef(null);
  // Aspect ratio of the loaded template image — used to size the wrapper that
  // holds the animated background video so the video matches the template
  // rectangle exactly (not the whole black caller area).
  const [tplAspectRatio, setTplAspectRatio] = useState(null);

  // Force the BG video to load + autoplay whenever the source changes. Some
  // browsers (and Playwright) ignore the `autoPlay` attribute when the element
  // is mounted with src already set, so we manually trigger load() + play().
  const bgVideoElRef = useRef(null);
  useEffect(() => {
    const v = bgVideoElRef.current;
    if (!v || !callerBgVideoSrc) return;
    try { v.load(); } catch {}
    // OBS Mode halves the decoding workload by playing at 0.5x speed.
    try { v.playbackRate = obsMode ? 0.5 : 1; } catch {}
    const tryPlay = () => {
      const p = v.play && v.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };
    tryPlay();
    const t = setTimeout(tryPlay, 400);
    return () => clearTimeout(t);
  }, [callerBgVideoSrc, obsMode]);
  const dragRef = useRef(null);
  const imgRef = useRef(null);
  const [imgNaturalW, setImgNaturalW] = useState(0);
  const [imgNaturalH, setImgNaturalH] = useState(0);

  // Auto-markup draggable position
  const [markupPos, setMarkupPos] = useState({ x: null, y: null });
  const markupDragRef = useRef(null);
  const markupInputRef = useRef(null);

  // Screen capture state
  const [captureStream, setCaptureStream] = useState(null);
  const videoRef = useRef(null);
  const [vidScale, setVidScale] = useState(100);
  const [vidOffset, setVidOffset] = useState({ x: 0, y: 0 });
  const vidDragRef = useRef(null);

  const startMarkupDrag = useCallback((e) => {
    if (e.target.tagName === 'INPUT') return;
    e.preventDefault();
    const startX = e.touches ? e.touches[0].clientX : e.clientX;
    const startY = e.touches ? e.touches[0].clientY : e.clientY;
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetX = startX - rect.left;
    const offsetY = startY - rect.top;
    markupDragRef.current = { offsetX, offsetY };

    const onMove = (ev) => {
      const cx = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const cy = ev.touches ? ev.touches[0].clientY : ev.clientY;
      setMarkupPos({
        x: Math.max(0, Math.min(window.innerWidth - 170, cx - markupDragRef.current.offsetX)),
        y: Math.max(0, Math.min(window.innerHeight - 50, cy - markupDragRef.current.offsetY)),
      });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
      if (markupInputRef.current) markupInputRef.current.focus();
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove);
    window.addEventListener('touchend', onUp);
  }, []);

  useEffect(() => {
    if (!canvasImage) return;
    const img = new Image();
    img.onload = () => { setImgNaturalW(img.naturalWidth); setImgNaturalH(img.naturalHeight); };
    img.src = canvasImage;
  }, [canvasImage]);

  // Attach stream to video element
  useEffect(() => {
    if (videoRef.current && captureStream) {
      videoRef.current.srcObject = captureStream;
    }
  }, [captureStream]);

  // Cleanup stream on unmount
  useEffect(() => {
    return () => {
      if (captureStream) captureStream.getTracks().forEach(t => t.stop());
    };
  }, [captureStream]);

  const startCapture = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
      setCaptureStream(stream);
      // Listen for user stopping via browser UI
      stream.getVideoTracks()[0].onended = () => setCaptureStream(null);
    } catch (e) {
      // User cancelled the picker
    }
  }, []);

  const stopCapture = useCallback(() => {
    if (captureStream) {
      captureStream.getTracks().forEach(t => t.stop());
      setCaptureStream(null);
    }
  }, [captureStream]);

  // Pan the video via drag
  const startVideoDrag = useCallback((e) => {
    e.preventDefault();
    const startX = e.touches ? e.touches[0].clientX : e.clientX;
    const startY = e.touches ? e.touches[0].clientY : e.clientY;
    const startOff = { ...vidOffset };
    vidDragRef.current = { startX, startY, startOff };

    const onMove = (ev) => {
      const cx = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const cy = ev.touches ? ev.touches[0].clientY : ev.clientY;
      setVidOffset({
        x: vidDragRef.current.startOff.x + (cx - vidDragRef.current.startX),
        y: vidDragRef.current.startOff.y + (cy - vidDragRef.current.startY),
      });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove);
    window.addEventListener('touchend', onUp);
  }, [vidOffset]);

  const startDrag = useCallback((e) => {
    e.preventDefault();
    const startY = e.touches ? e.touches[0].clientY : e.clientY;
    const startPercent = callerPercent;
    dragRef.current = { startY, startPercent };

    const onMove = (ev) => {
      const clientY = ev.touches ? ev.touches[0].clientY : ev.clientY;
      const vh = window.innerHeight;
      const deltaPercent = ((dragRef.current.startY - clientY) / vh) * 100;
      const newPercent = Math.max(10, Math.min(60, dragRef.current.startPercent + deltaPercent));
      setCallerPercent(newPercent);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove);
    window.addEventListener('touchend', onUp);
  }, [callerPercent]);

  const topBarH = 24;
  const handleH = 12;
  const templateAreaH = (window.innerHeight - topBarH) * ((100 - callerPercent) / 100);
  const callerAreaH = (window.innerHeight - topBarH) * (callerPercent / 100);

  let renderedImgW = '100%';
  if (imgNaturalW > 0 && imgNaturalH > 0) {
    const availH = templateAreaH - handleH;
    const containerW = window.innerWidth;
    const scaleW = containerW / imgNaturalW;
    const scaleH = availH / imgNaturalH;
    const scale = Math.min(scaleW, scaleH);
    renderedImgW = Math.floor(imgNaturalW * scale);
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-black" data-testid="bingo-caller-page">
      {/* Top bar with mode toggle */}
      <div className="flex items-center gap-2 px-2 py-1 bg-black/90 border-b border-neutral-800 z-20 flex-shrink-0" style={{ height: topBarH }}>
        <button data-testid="caller-back-btn" onClick={onBack}
          className="flex items-center gap-1 text-gray-400 hover:text-white transition-colors text-[10px] font-bold">
          <ArrowLeft className="w-3 h-3" /> Back
        </button>
        <span className="text-white font-black text-xs">BingoGrid AI</span>

        {/* Mode toggle */}
        <div className="flex bg-neutral-800 rounded overflow-hidden ml-1">
          <button data-testid="caller-mode-bingo"
            onClick={() => setCallerMode('bingo')}
            className={`px-2 py-0.5 text-[9px] font-black transition-colors ${callerMode === 'bingo' ? 'bg-cyan-500 text-black' : 'text-gray-400 hover:text-white'}`}>
            BINGO
          </button>
          <button data-testid="caller-mode-loteria"
            onClick={() => setCallerMode('loteria')}
            className={`px-2 py-0.5 text-[9px] font-black transition-colors ${callerMode === 'loteria' ? 'bg-amber-500 text-black' : 'text-gray-400 hover:text-white'}`}>
            LOTERIA
          </button>
          <button data-testid="caller-mode-external"
            onClick={() => setCallerMode('external')}
            className={`px-2 py-0.5 text-[9px] font-black transition-colors flex items-center gap-0.5 ${callerMode === 'external' ? 'bg-emerald-500 text-black' : 'text-gray-400 hover:text-white'}`}>
            <Monitor className="w-2.5 h-2.5" /> APP
          </button>
        </div>

        <span className={`text-[10px] font-bold ${callerMode === 'loteria' ? 'text-amber-400' : callerMode === 'external' ? 'text-emerald-400' : 'text-cyan-400'}`}>
          {callerMode === 'loteria' ? 'Baraja de Loteria' : callerMode === 'external' ? 'App Externa' : 'Live Caller'}
        </span>
      </div>

      {/* Content area */}
      <div className="flex-1 flex flex-col overflow-hidden" ref={containerRef}>
        {/* Template area */}
        <div className="overflow-hidden flex items-center justify-center bg-black relative"
          style={{ height: templateAreaH }}>
          {canvasImage ? (
            <div
              className="relative"
              style={{
                aspectRatio: tplAspectRatio || undefined,
                maxWidth: '100%',
                maxHeight: '100%',
                width: tplAspectRatio ? 'auto' : undefined,
                height: tplAspectRatio ? '100%' : undefined,
              }}
            >
              {/* Animated background — sized to match the template image so it
                  IS the template's background. Set Step 1 → Background to
                  transparent so the video shows through where boards aren't. */}
              {callerBgVideoSrc && (
                <video
                  ref={bgVideoElRef}
                  key={callerBgVideoSrc}
                  src={callerBgVideoSrc}
                  autoPlay loop muted playsInline preload="auto"
                  disablePictureInPicture
                  disableRemotePlayback
                  className="absolute inset-0 w-full h-full pointer-events-none"
                  style={{
                    objectFit: callerBgVideoFit,
                    opacity: (callerBgVideoOpacity || 60) / 100,
                    willChange: 'transform',
                    transform: obsMode ? 'translateZ(0)' : undefined,
                    contain: obsMode ? 'paint' : undefined,
                  }}
                  data-testid="caller-bg-video"
                />
              )}
              <img
                ref={imgRef}
                src={canvasImage}
                alt="Template"
                onLoad={(e) => {
                  const img = e.target;
                  if (img.naturalWidth && img.naturalHeight) {
                    setTplAspectRatio(img.naturalWidth / img.naturalHeight);
                  }
                }}
                className="absolute inset-0 w-full h-full object-contain"
              />
            </div>
          ) : (
            <div className="text-gray-500 text-sm font-bold text-center relative">
              <p>No template loaded</p>
              <p className="text-xs mt-1">Go back and create boards first</p>
            </div>
          )}

          {/* Auto-markup overlay — draggable, always on top, auto-focused */}
          <div
            className="fixed z-50 w-40 bg-black/85 backdrop-blur-sm rounded-lg border border-neutral-600 p-2 space-y-1.5 cursor-grab active:cursor-grabbing select-none"
            style={markupPos.x !== null ? { left: markupPos.x, top: markupPos.y } : { top: topBarH + 4, right: 4 }}
            onMouseDown={startMarkupDrag}
            onTouchStart={startMarkupDrag}
          >
            <h3 className="text-white font-black text-[9px] pointer-events-none">AUTO-MARKUP</h3>
            <AutoMarkupInput
              ref={markupInputRef}
              data-testid="app-auto-markup-input"
              placeholder={gameMode === 'loteria' ? 'Card # or name...' : 'e.g. B7, N35...'}
              onSubmit={handleAutoMarkupSubmit}
              resetKey={autoMarkupResetKey}
              variant="native"
              className="w-full px-2 py-1 bg-neutral-800 border border-neutral-600 rounded text-white text-[10px] placeholder-gray-500 focus:outline-none focus:border-emerald-500 cursor-text"
            />
            <div className="flex items-center justify-between pointer-events-auto">
              <span className="text-[8px] font-bold text-gray-500">
                Called ({calledNumbers?.length || 0})
              </span>
              {calledNumbers?.length > 0 && (
                <button onClick={(e) => { e.stopPropagation(); onCallerReset(); }}
                  className="text-red-500 hover:text-red-400 transition-colors" title="Clear All">
                  <Trash2 className="w-2.5 h-2.5" />
                </button>
              )}
            </div>
            {calledNumbers?.length > 0 && (
              <div className="flex flex-wrap gap-0.5 max-h-[60px] overflow-y-auto pointer-events-none">
                {[...calledNumbers].reverse().map((n, i) => (
                  <span key={i} className="text-[7px] font-bold px-1 py-0.5 rounded bg-emerald-900/50 text-emerald-400">
                    {n}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Drag handle */}
        <div
          className="flex-shrink-0 cursor-row-resize flex items-center justify-center bg-neutral-900 hover:bg-neutral-700 transition-colors z-10"
          style={{ height: handleH }}
          onMouseDown={startDrag}
          onTouchStart={startDrag}
          data-testid="caller-drag-handle"
        >
          <GripHorizontal className="w-5 h-2.5 text-neutral-500" />
        </div>

        {/* Caller section - width matched to template */}
        <div className="flex-shrink-0 overflow-hidden flex justify-center bg-black"
          style={{ height: callerAreaH - handleH }}>
          <div style={{ width: typeof renderedImgW === 'number' ? renderedImgW : '100%', maxWidth: '100%' }}>
            {callerMode === 'external' ? (
              /* Screen capture centered + auto-markup floating top-right */
              <div className="w-full h-full relative">
                {/* Video feed area — full width, centered, zoomable & pannable */}
                <div className="w-full h-full flex items-center justify-center overflow-hidden"
                  style={{ background: '#000' }}>
                  {captureStream ? (
                    <div className="w-full h-full overflow-hidden cursor-grab active:cursor-grabbing group"
                      onMouseDown={startVideoDrag}
                      onTouchStart={startVideoDrag}>
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        className="w-full h-full"
                        style={{
                          objectFit: 'contain',
                          transform: `scale(${vidScale / 100}) translate(${vidOffset.x / (vidScale / 100)}px, ${vidOffset.y / (vidScale / 100)}px)`,
                          transformOrigin: 'center center',
                        }}
                      />
                      {/* Zoom controls — hidden until hover, stops propagation to prevent pan */}
                      <div className="absolute bottom-1 left-1 right-1 flex items-center gap-1 bg-black/70 rounded px-1.5 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20"
                        onMouseDown={(e) => e.stopPropagation()}
                        onTouchStart={(e) => e.stopPropagation()}>
                        <span className="text-[8px] text-gray-400 font-bold flex-shrink-0">Zoom</span>
                        <input type="range" min={50} max={300} step={5} value={vidScale}
                          onChange={(e) => setVidScale(Number(e.target.value))}
                          className="flex-1 h-1 cursor-pointer accent-emerald-500" />
                        <span className="text-[8px] text-emerald-400 font-bold w-8 text-right">{vidScale}%</span>
                        <button onClick={() => { setVidScale(100); setVidOffset({ x: 0, y: 0 }); }}
                          className="text-[8px] text-gray-500 hover:text-white font-bold ml-1">Reset</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center gap-2">
                      <Monitor className="w-10 h-10 text-emerald-500/30" />
                      <p className="text-emerald-400/50 text-xs font-bold">Baraja de Loteria</p>
                      <button
                        data-testid="start-capture-btn"
                        onClick={startCapture}
                        className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-black rounded hover:bg-emerald-500 transition-colors flex items-center gap-1"
                      >
                        <Monitor className="w-3 h-3" /> Capture Window
                      </button>
                    </div>
                  )}
                </div>

                {/* Stop capture — top-left */}
                {captureStream && (
                  <div className="absolute top-1 left-1 z-20"
                    onMouseDown={(e) => e.stopPropagation()}>
                    <button onClick={stopCapture}
                      className="px-2 py-0.5 bg-red-600/80 text-white text-[9px] font-bold rounded hover:bg-red-500 flex items-center gap-1">
                      <MonitorOff className="w-2.5 h-2.5" /> Stop
                    </button>
                  </div>
                )}
              </div>
            ) : callerMode === 'loteria' ? (
              <LoteriaCallerDraw onAutoMarkup={onBallDrawn} onReset={onCallerReset} figures={loteriaFigures} className="w-full h-full" />
            ) : (
              <BingoBallDraw onAutoMarkup={onBallDrawn} onReset={onCallerReset} className="w-full h-full" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
